import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { WidgetErrorBoundary } from '../insights/widgets/WidgetErrorBoundary'
import {
  listNotificationDeliveries,
  listNotificationPreferences,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  upsertNotificationPreference,
} from '../../api/notifications'
import { ApiError } from '../../api/problem'
import { label } from '../../lib/labels'
import {
  CHANNEL_KINDS,
  CHANNEL_KIND_LABEL,
  DIGEST_FREQUENCIES,
  DIGEST_FREQUENCY_LABEL,
  type ChannelKind,
  type DeliveryView,
  type DigestFrequency,
  type NotificationPreferenceView,
  type NotificationView,
} from '../../types/notification'

type Tab = 'notifications' | 'preferences'
type ReadFilter = 'all' | 'unread'

const SEVERITY_VARIANT: Record<string, StatusPillVariant> = {
  info: 'info',
  warning: 'warning',
  critical: 'danger',
}

const DELIVERY_STATUS_VARIANT: Record<string, StatusPillVariant> = {
  queued: 'neutral',
  sent: 'info',
  delivered: 'info',
  read: 'success',
  failed: 'danger',
  suppressed: 'muted',
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? (err.problem.detail ?? err.message) : fallback
}

/**
 * The Notifications center (`/my-work/notifications`) — surfaces `module.notify`'s in-app
 * notification feed (`GET /api/v1/notifications`, own-principal implicit) plus the caller's own
 * notification preferences (`GET`/`PUT /api/v1/notification-preferences`). Every action here is a
 * real backend call: mark-read/mark-all-read and the preference upsert are all wired (CLAUDE.md §4
 * "no dead buttons").
 *
 * Deliberately NOT built here (disclosed, not silently dropped — docs/api-spec.md §23):
 * `notification-subscriptions` ("follow a record") and the tenant-wide `notification-channels`
 * admin config have no v1 REST surface, so no UI element for either exists on this screen. The
 * per-notification `GET /{id}/deliveries` read (IB-166, a thin additive GET this story adds) is
 * wired lazily, per row, on demand via the "Channels" disclosure toggle below — not eagerly for
 * every row on load.
 */
export function NotificationsScreen() {
  const [tab, setTab] = useState<Tab>('notifications')

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-3)', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--iw-accent)' }}>
          <Icon name="bell" size={22} />
        </span>
        <h1 style={{ margin: 0, fontSize: 'var(--iw-size-xl)' }}>Notifications</h1>
        <span style={{ flex: 1 }} />
        <div role="group" aria-label="Section">
          <button
            type="button"
            className="iw-chip"
            aria-pressed={tab === 'notifications'}
            onClick={() => setTab('notifications')}
          >
            <Icon name="inbox" size={14} /> Notifications
          </button>{' '}
          <button
            type="button"
            className="iw-chip"
            aria-pressed={tab === 'preferences'}
            onClick={() => setTab('preferences')}
          >
            <Icon name="sliders" size={14} /> Preferences
          </button>
        </div>
      </div>

      {tab === 'notifications' ? <NotificationsList /> : <PreferencesPanel />}
    </div>
  )
}

function NotificationsList() {
  const [notifications, setNotifications] = useState<NotificationView[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [readFilter, setReadFilter] = useState<ReadFilter>('all')
  const [categoryFilter, setCategoryFilter] = useState('')

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true)
      setError(null)
      const data = await listNotifications(signal)
      setNotifications(data)
    } catch (err) {
      setError(errorMessage(err, 'Could not load notifications. Check your connection and try again.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  const categories = useMemo(
    () => Array.from(new Set(notifications.map((n) => n.category))).sort(),
    [notifications],
  )

  const filtered = useMemo(
    () =>
      notifications.filter((n) => {
        if (readFilter === 'unread' && n.deliveryStatus === 'read') return false
        if (categoryFilter && n.category !== categoryFilter) return false
        return true
      }),
    [notifications, readFilter, categoryFilter],
  )

  const unreadCount = useMemo(() => notifications.filter((n) => n.deliveryStatus !== 'read').length, [notifications])
  const [expandedId, setExpandedId] = useState<string | null>(null)

  async function handleMarkRead(id: string) {
    setActionError(null)
    try {
      await markNotificationRead(id)
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, deliveryStatus: 'read' } : n)),
      )
    } catch (err) {
      setActionError(errorMessage(err, 'Could not mark this notification read.'))
    }
  }

  async function handleMarkAllRead() {
    setActionError(null)
    try {
      await markAllNotificationsRead()
      setNotifications((prev) => prev.map((n) => ({ ...n, deliveryStatus: 'read' })))
    } catch (err) {
      setActionError(errorMessage(err, 'Could not mark every notification read.'))
    }
  }

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div role="group" aria-label="Read filter" style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
          {(['all', 'unread'] as ReadFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              className="iw-chip"
              aria-pressed={readFilter === f}
              onClick={() => setReadFilter(f)}
            >
              {f === 'all' ? 'All' : `Unread (${unreadCount})`}
            </button>
          ))}
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="notif-category-filter">
            Type
          </label>
          <select
            id="notif-category-filter"
            className="iw-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All types</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {label('notificationCategory', c)}
              </option>
            ))}
          </select>
        </div>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="iw-btn"
          onClick={() => void handleMarkAllRead()}
          disabled={loading || unreadCount === 0}
        >
          <Icon name="check" size={14} /> Mark all read
        </button>
      </div>

      {actionError ? <InlineAlert severity="error">{actionError}</InlineAlert> : null}

      <WidgetErrorBoundary title="Notifications" icon="bell">
        {error ? (
          <InlineAlert
            severity="error"
            action={
              <button type="button" className="iw-btn iw-btn--sm" onClick={() => void load()}>
                Try again
              </button>
            }
          >
            {error}
          </InlineAlert>
        ) : loading ? (
          <p style={{ color: 'var(--iw-text-muted)' }}>Loading notifications…</p>
        ) : filtered.length === 0 ? (
          <div style={{ color: 'var(--iw-text-muted)', textAlign: 'center', padding: 'var(--iw-space-6)' }}>
            <Icon name="inbox" size={28} />
            <p>{notifications.length === 0 ? 'No notifications yet.' : 'No notifications match this filter.'}</p>
          </div>
        ) : (
          <table className="iw-table" aria-label="Notifications">
            <thead>
              <tr>
                <th scope="col">Type</th>
                <th scope="col">Notification</th>
                <th scope="col">Severity</th>
                <th scope="col">Delivery status</th>
                <th scope="col">When</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((n) => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  expanded={expandedId === n.id}
                  onToggleExpanded={() => setExpandedId((cur) => (cur === n.id ? null : n.id))}
                  onMarkRead={() => void handleMarkRead(n.id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </WidgetErrorBoundary>
    </>
  )
}

interface NotificationRowProps {
  notification: NotificationView
  expanded: boolean
  onToggleExpanded: () => void
  onMarkRead: () => void
}

/** One notification row + its lazily-fetched, on-demand per-channel delivery breakdown
 * (`GET /notifications/{id}/deliveries`, IB-166's thin additive GET). */
function NotificationRow({ notification: n, expanded, onToggleExpanded, onMarkRead }: NotificationRowProps) {
  return (
    <>
      <tr>
        <td>{label('notificationCategory', n.category)}</td>
        <td>
          <div>{n.title}</div>
          {n.renderedSummary ? (
            <div style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>{n.renderedSummary}</div>
          ) : null}
        </td>
        <td>
          <StatusPill variant={SEVERITY_VARIANT[n.severity] ?? 'neutral'} label={n.severity} />
        </td>
        <td>
          <StatusPill variant={DELIVERY_STATUS_VARIANT[n.deliveryStatus] ?? 'neutral'} label={n.deliveryStatus} />
        </td>
        <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
          {formatTimestamp(n.createdAt)}
        </td>
        <td style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
          {n.deliveryStatus !== 'read' ? (
            <button type="button" className="iw-btn iw-btn--sm" onClick={onMarkRead}>
              Mark read
            </button>
          ) : (
            <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>Read</span>
          )}
          <button type="button" className="iw-btn iw-btn--sm" onClick={onToggleExpanded} aria-expanded={expanded}>
            <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={12} /> Channels
          </button>
        </td>
      </tr>
      {expanded ? (
        <tr>
          <td colSpan={6} style={{ background: 'var(--iw-accent-subtle)' }}>
            <DeliveriesDisclosure notificationId={n.id} />
          </td>
        </tr>
      ) : null}
    </>
  )
}

/** Lazily fetches `GET /notifications/{id}/deliveries` the first time it renders (i.e. the first
 * time its row is expanded) — never eagerly for every row on load. */
function DeliveriesDisclosure({ notificationId }: { notificationId: string }) {
  const [deliveries, setDeliveries] = useState<DeliveryView[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    listNotificationDeliveries(notificationId, controller.signal)
      .then(setDeliveries)
      .catch((err) => setError(errorMessage(err, 'Could not load delivery detail for this notification.')))
    return () => controller.abort()
  }, [notificationId])

  if (error) {
    return <InlineAlert severity="error">{error}</InlineAlert>
  }
  if (!deliveries) {
    return <p style={{ color: 'var(--iw-text-muted)', margin: 0 }}>Loading channel detail…</p>
  }
  if (deliveries.length === 0) {
    return <p style={{ color: 'var(--iw-text-muted)', margin: 0 }}>No delivery rows for this notification.</p>
  }
  return (
    <div style={{ display: 'flex', gap: 'var(--iw-space-4)', flexWrap: 'wrap' }}>
      {deliveries.map((d) => (
        <span key={d.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--iw-space-1)' }}>
          <Icon name={d.channelKind === 'email' ? 'mail' : 'bell'} size={14} />
          {CHANNEL_KIND_LABEL[d.channelKind as ChannelKind] ?? d.channelKind}:
          <StatusPill variant={DELIVERY_STATUS_VARIANT[d.status] ?? 'neutral'} label={d.status} />
          {d.attempts > 0 ? (
            <span style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
              ({d.attempts} attempt{d.attempts === 1 ? '' : 's'})
            </span>
          ) : null}
        </span>
      ))}
    </div>
  )
}

function PreferencesPanel() {
  const [preferences, setPreferences] = useState<NotificationPreferenceView[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [category, setCategory] = useState('')
  const [channelKind, setChannelKind] = useState<ChannelKind>('in_app')
  const [optIn, setOptIn] = useState(true)
  const [digest, setDigest] = useState<DigestFrequency>('none')

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true)
      setError(null)
      const data = await listNotificationPreferences(signal)
      setPreferences(data)
    } catch (err) {
      setError(errorMessage(err, 'Could not load notification preferences.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  async function upsert(next: { category: string; channelKind: string; optIn: boolean; digest: string }) {
    setActionError(null)
    setSaving(true)
    try {
      const saved = await upsertNotificationPreference(next)
      setPreferences((prev) => {
        const idx = prev.findIndex((p) => p.id === saved.id)
        if (idx >= 0) {
          const copy = [...prev]
          copy[idx] = saved
          return copy
        }
        return [...prev, saved]
      })
    } catch (err) {
      setActionError(errorMessage(err, 'Could not save this preference.'))
    } finally {
      setSaving(false)
    }
  }

  async function handleAdd(e: FormEvent) {
    e.preventDefault()
    if (!category.trim()) {
      setActionError('Enter a category (e.g. "task_assigned", "time_critical").')
      return
    }
    await upsert({ category: category.trim(), channelKind, optIn, digest })
    setCategory('')
  }

  async function handleToggle(pref: NotificationPreferenceView) {
    await upsert({ category: pref.category, channelKind: pref.channelKind, optIn: !pref.optIn, digest: pref.digest })
  }

  return (
    <>
      {actionError ? <InlineAlert severity="error">{actionError}</InlineAlert> : null}

      <form
        onSubmit={(e) => void handleAdd(e)}
        style={{ display: 'flex', gap: 'var(--iw-space-2)', flexWrap: 'wrap', alignItems: 'flex-end' }}
      >
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="pref-category">
            Category
          </label>
          <input
            id="pref-category"
            className="iw-input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g. task_assigned"
          />
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="pref-channel">
            Channel
          </label>
          <select
            id="pref-channel"
            className="iw-select"
            value={channelKind}
            onChange={(e) => setChannelKind(e.target.value as ChannelKind)}
          >
            {CHANNEL_KINDS.map((c) => (
              <option key={c} value={c}>
                {CHANNEL_KIND_LABEL[c]}
              </option>
            ))}
          </select>
        </div>
        <div className="iw-field">
          <label className="iw-field-label" htmlFor="pref-digest">
            Delivery
          </label>
          <select
            id="pref-digest"
            className="iw-select"
            value={digest}
            onChange={(e) => setDigest(e.target.value as DigestFrequency)}
          >
            {DIGEST_FREQUENCIES.map((d) => (
              <option key={d} value={d}>
                {DIGEST_FREQUENCY_LABEL[d]}
              </option>
            ))}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-1)' }}>
          <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)} /> Opted in
        </label>
        <button type="submit" className="iw-btn iw-btn--primary" disabled={saving}>
          <Icon name="plus" size={14} /> Save preference
        </button>
      </form>

      <WidgetErrorBoundary title="Notification preferences" icon="sliders">
        {error ? (
          <InlineAlert
            severity="error"
            action={
              <button type="button" className="iw-btn iw-btn--sm" onClick={() => void load()}>
                Try again
              </button>
            }
          >
            {error}
          </InlineAlert>
        ) : loading ? (
          <p style={{ color: 'var(--iw-text-muted)' }}>Loading preferences…</p>
        ) : preferences.length === 0 ? (
          <div style={{ color: 'var(--iw-text-muted)', textAlign: 'center', padding: 'var(--iw-space-6)' }}>
            <Icon name="sliders" size={28} />
            <p>
              No explicit overrides yet — every category/channel currently follows the tenant default
              (in-app always on). Add one above to override it for yourself.
            </p>
          </div>
        ) : (
          <table className="iw-table" aria-label="Notification preferences">
            <thead>
              <tr>
                <th scope="col">Category</th>
                <th scope="col">Channel</th>
                <th scope="col">Opted in</th>
                <th scope="col">Delivery</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {preferences.map((p) => (
                <tr key={p.id}>
                  <td>{label('notificationCategory', p.category)}</td>
                  <td>{CHANNEL_KIND_LABEL[p.channelKind as ChannelKind] ?? p.channelKind}</td>
                  <td>
                    <StatusPill variant={p.optIn ? 'success' : 'muted'} label={p.optIn ? 'Opted in' : 'Opted out'} />
                  </td>
                  <td>{DIGEST_FREQUENCY_LABEL[p.digest as DigestFrequency] ?? p.digest}</td>
                  <td>
                    <button type="button" className="iw-btn iw-btn--sm" onClick={() => void handleToggle(p)}>
                      {p.optIn ? 'Opt out' : 'Opt back in'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </WidgetErrorBoundary>
    </>
  )
}
